import { liesBase64, schreibeBase64 } from './dateien';
import { ausBase64 } from './webcrypto';

/**
 * Der Vorschaustreifen im Browser.
 *
 * ⚠️ **Die native Begründung gilt hier nicht.** `videoFrames.ts` sagt, warum die Bilder auf
 * dem Gerät **nicht** im WebView entstehen: die Aufnahme müsste als Datenadresse durch den
 * Speicher, und auf iOS 15 ist genau das die enge Stelle. Am Schreibtisch gibt es diese Enge
 * nicht — dort ist der Weg über `<video>` und `<canvas>` der einzige, und er ist gangbar.
 *
 * ⚠️ **Die Nummer im Dateinamen ist die Stelle in `msList`, nicht die Zählung der geglückten
 * Bilder.** Genau so macht es `VideoFramesPlugin.swift` (`for (nr, ms) in msListe.enumerated()`).
 * Fällt eines aus, entsteht eine Lücke in den Namen und ein kürzeres Feld — beides ist
 * Absicht, und beide Seiten müssen es gleich halten.
 */

export interface FrameWebDeps {
  /** Die Aufnahme als Base64 aus Documents. */
  liesDatei?: (pfad: string) => Promise<string>;
  /** Je Zeitpunkt ein JPEG als Base64, oder `null`, wenn es dort nichts zu holen gab. */
  bilder?: (bytes: Blob, msList: number[], breite: number) => Promise<(string | null)[]>;
  schreibe?: (pfad: string, inhalt: string) => Promise<void>;
}

export const framePfad = (ordner: string, nr: number) => ordner + '/' + String(nr) + '.jpg';

/**
 * Zieht die Einzelbilder und legt sie ab.
 *
 * Herausgelöst, damit sich das Auslassen, die Reihenfolge und die Namen ohne Browser prüfen
 * lassen — gezeichnet wird nur in `leinwandBilder`.
 */
export async function zieheFramesImBrowser(
  optionen: { path: string; ordner: string; msList: number[]; breite: number },
  deps: Required<FrameWebDeps>,
): Promise<{ pfade: string[] }> {
  const roh = await deps.liesDatei(optionen.path);
  if (roh === '') return { pfade: [] };

  const bytes = new Blob([ausBase64(roh)]);
  const gezogen = await deps.bilder(bytes, optionen.msList, optionen.breite);

  const pfade: string[] = [];
  for (const [nr, bild] of gezogen.entries()) {
    if (!bild) continue;
    const pfad = framePfad(optionen.ordner, nr);
    // Ein einzelnes misslungenes Schreiben kostet nur dieses Bild. Der Streifen ist eine
    // Ahnung, keine Bedingung (s. `VideoAnnotateSheet::masseStehen`).
    try {
      await deps.schreibe(pfad, bild);
      pfade.push(pfad);
    } catch {
      continue;
    }
  }
  return { pfade };
}

/**
 * Die Einzelbilder aus einem `<video>` auf eine `<canvas>`.
 *
 * ⚠️ **Ein Element für alle Zeitpunkte**, nacheinander angesprungen. Zehn `<video>`-Elemente
 * nebeneinander dekodierten dieselbe Datei zehnmal.
 */
export async function leinwandBilder(
  bytes: Blob,
  msList: number[],
  breite: number,
): Promise<(string | null)[]> {
  const adresse = URL.createObjectURL(bytes);
  const el = document.createElement('video');
  el.muted = true;
  el.playsInline = true;
  el.preload = 'auto';

  try {
    const bereit = new Promise<boolean>((fertig) => {
      el.onloadeddata = () => fertig(true);
      el.onerror = () => fertig(false);
    });
    el.src = adresse;
    if (!(await bereit)) return msList.map(() => null);

    const leinwand = document.createElement('canvas');
    const stift = leinwand.getContext('2d');
    const hoehe = el.videoWidth > 0 ? Math.round((breite * el.videoHeight) / el.videoWidth) : 0;
    if (!stift || hoehe === 0) return msList.map(() => null);
    leinwand.width = breite;
    leinwand.height = hoehe;

    const aus: (string | null)[] = [];
    for (const ms of msList) {
      const gesprungen = await new Promise<boolean>((fertig) => {
        el.onseeked = () => fertig(true);
        el.onerror = () => fertig(false);
        // Über das Ende hinaus zu springen bliebe ohne `seeked` stehen — das wäre ein
        // Aufhänger, kein fehlendes Bild.
        el.currentTime = Math.min(ms / 1000, Math.max((el.duration || 0) - 0.05, 0));
      });
      if (!gesprungen) {
        aus.push(null);
        continue;
      }
      stift.drawImage(el, 0, 0, breite, hoehe);
      aus.push(leinwand.toDataURL('image/jpeg', 0.6).split(',')[1] ?? null);
    }
    return aus;
  } catch {
    return msList.map(() => null);
  } finally {
    URL.revokeObjectURL(adresse);
  }
}

export function baueFrameDeps(deps: FrameWebDeps = {}): Required<FrameWebDeps> {
  return {
    liesDatei:
      deps.liesDatei ??
      ((pfad) => liesBase64(pfad)),
    bilder: deps.bilder ?? leinwandBilder,
    schreibe:
      deps.schreibe ??
      ((pfad, inhalt) => schreibeBase64(pfad, inhalt, { ordnerAnlegen: true })),
  };
}
